/**
 * Registrations Store - Course Registration State Management
 *
 * Handles:
 * - Loading participants for a course
 * - Caching in IndexedDB
 */

import { defineStore } from 'pinia'
import { ref, computed, toRaw } from 'vue'
import { registrationsApi } from '@/api/registrations.api'
import { db } from '@/db'
import { isNetworkError } from '@/api/client'
import type { CourseRegistrationDto } from '@/types/api.types'

export const useRegistrationsStore = defineStore('registrations', () => {
  // ==========================================================================
  // State
  // ==========================================================================

  const registrations = ref<Map<number, CourseRegistrationDto[]>>(new Map())
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastFetched = ref<Map<number, number>>(new Map())

  // Cache duration: 10 minutes (participants change less often)
  const CACHE_DURATION = 10 * 60 * 1000

  // ==========================================================================
  // Getters
  // ==========================================================================

  /**
   * Get registrations for a specific course
   */
  const getByCourseId = computed(() => {
    return (courseId: number): CourseRegistrationDto[] => {
      return registrations.value.get(courseId) || []
    }
  })

  function nachName(a: CourseRegistrationDto, b: CourseRegistrationDto): number {
    // Sort by last name, then first name
    const lastNameCompare = (a.participantLastName || '').localeCompare(b.participantLastName || '')
    if (lastNameCompare !== 0) return lastNameCompare
    return (a.participantFirstName || '').localeCompare(b.participantFirstName || '')
  }

  /**
   * Get active registrations for a course — nur aktuelle Mitglieder. Ausgetretene liefert
   * das Backend mit, sie gehören aber nicht in Zähler, Kursdetail oder Teilnehmerliste.
   */
  const getActiveByCourseId = computed(() => {
    return (courseId: number): CourseRegistrationDto[] => {
      const courseRegistrations = registrations.value.get(courseId) || []
      return courseRegistrations.filter((r) => r.isActive && !r.isHistorical).sort(nachName)
    }
  })

  /**
   * Teilnehmer eines Termins. Maßgeblich ist das Termindatum: bis einschließlich
   * Austrittstag war ein ausgetretenes Mitglied ein normales Mitglied und gehört dazu,
   * bei späteren Terminen nicht. Ohne Austrittsdatum lässt sich das nicht entscheiden —
   * dann zählt die Person nicht dazu (in Produktion am 2026-10-21: 0 solche Fälle).
   */
  const getForSessionDate = computed(() => {
    return (courseId: number, sessionDate: string): CourseRegistrationDto[] => {
      const termin = sessionDate.slice(0, 10)
      const courseRegistrations = registrations.value.get(courseId) || []
      return courseRegistrations
        .filter(
          (r) =>
            r.isActive &&
            (!r.isHistorical ||
              (!!r.membershipEndedOn && termin <= r.membershipEndedOn.slice(0, 10)))
        )
        .sort(nachName)
    }
  })

  /**
   * Get participant count for a course
   */
  const getCountByCourseId = computed(() => {
    return (courseId: number): number => {
      return getActiveByCourseId.value(courseId).length
    }
  })

  /**
   * Get all participants across all courses (deduplicated by participantId)
   */
  const getAllParticipants = computed(() => {
    const allRegistrations: CourseRegistrationDto[] = []
    registrations.value.forEach((courseRegs) => {
      allRegistrations.push(...courseRegs)
    })

    // Deduplicate by participantId, keeping the first occurrence
    const seen = new Set<number>()
    return allRegistrations
      .filter((r) => r.isActive && !r.isHistorical)
      .filter((r) => {
        if (seen.has(r.participantId)) return false
        seen.add(r.participantId)
        return true
      })
      .sort((a, b) => {
        const lastNameCompare = (a.participantLastName || '').localeCompare(
          b.participantLastName || ''
        )
        if (lastNameCompare !== 0) return lastNameCompare
        return (a.participantFirstName || '').localeCompare(b.participantFirstName || '')
      })
  })

  /**
   * Get a single registration by participant ID
   */
  const getByParticipantId = computed(() => {
    return (participantId: number): CourseRegistrationDto | undefined => {
      for (const courseRegs of registrations.value.values()) {
        const found = courseRegs.find((r) => r.participantId === participantId)
        if (found) return found
      }
      return undefined
    }
  })

  // ==========================================================================
  // Actions
  // ==========================================================================

  /**
   * Fetch registrations for a course
   */
  async function fetchByCourse(courseId: number, forceRefresh = false): Promise<void> {
    // Check cache validity
    const lastFetch = lastFetched.value.get(courseId) || 0
    const isCacheValid = Date.now() - lastFetch < CACHE_DURATION

    if (isCacheValid && !forceRefresh && registrations.value.has(courseId)) {
      return
    }

    // Offline + data already in memory → skip API & IndexedDB roundtrip
    if (!navigator.onLine && registrations.value.has(courseId)) return

    isLoading.value = true
    error.value = null

    try {
      // Try API first
      const data = await registrationsApi.getByCourse(courseId)

      // Update state
      registrations.value.set(courseId, data)
      lastFetched.value.set(courseId, Date.now())

      // Cache in IndexedDB
      await cacheRegistrationsLocally(courseId, data)
    } catch (e) {
      // Try to load from IndexedDB on network error
      if (isNetworkError(e)) {
        const cached = await loadFromCache(courseId)
        if (cached.length > 0) {
          registrations.value.set(courseId, cached)
          error.value = null
          return
        }
      }

      error.value = e instanceof Error ? e.message : 'Fehler beim Laden der Teilnehmer'
      throw e
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Cache registrations in IndexedDB
   */
  async function cacheRegistrationsLocally(
    courseId: number,
    data: CourseRegistrationDto[]
  ): Promise<void> {
    try {
      await db.transaction('rw', db.registrations, async () => {
        // Remove old registrations for this course
        await db.registrations.where('courseId').equals(courseId).delete()
        // Add new registrations — toRaw, weil Einträge aus `registrations`
        // Vue-Proxys sind und IndexedDB sie nicht klonen kann (DataCloneError)
        await db.registrations.bulkPut(data.map((r) => toRaw(r)))
      })
    } catch (e) {
      console.warn('[RegistrationsStore] Failed to cache registrations:', e)
    }
  }

  /**
   * Load registrations from IndexedDB cache
   */
  async function loadFromCache(courseId: number): Promise<CourseRegistrationDto[]> {
    try {
      return await db.registrations.where('courseId').equals(courseId).toArray()
    } catch (e) {
      console.warn('[RegistrationsStore] Failed to load from cache:', e)
      return []
    }
  }

  /**
   * Create a provisional course registration (Online-Only).
   * After creation, refreshes the local cache for that course.
   */
  async function createRegistration(
    courseId: number,
    personId: number,
    isProvisional = true
  ): Promise<CourseRegistrationDto> {
    const created = await registrationsApi.create({ courseId, personId, isProvisional })

    // Add to in-memory state immediately
    const current = registrations.value.get(courseId) || []
    registrations.value.set(courseId, [...current, created])

    // Update IndexedDB cache
    await cacheRegistrationsLocally(courseId, registrations.value.get(courseId)!)

    return created
  }

  /**
   * Promote a provisional registration to regular (Online-Only).
   */
  async function promoteRegistration(courseId: number, participantId: number): Promise<void> {
    await registrationsApi.update(courseId, participantId, {
      courseId,
      personId: participantId,
      isProvisional: false,
    })

    // Update in-memory state
    const current = registrations.value.get(courseId) || []
    const updated = current.map((r) =>
      r.participantId === participantId ? { ...r, isProvisional: false } : r
    )
    registrations.value.set(courseId, updated)

    // Update IndexedDB cache
    await cacheRegistrationsLocally(courseId, updated)
  }

  /**
   * Remove a course registration via soft-delete (Online-Only).
   * Attendances are NOT affected (no cascade in backend).
   */
  async function removeRegistration(courseId: number, participantId: number): Promise<void> {
    await registrationsApi.remove(courseId, participantId)

    // Remove from in-memory state
    const current = registrations.value.get(courseId) || []
    const updated = current.filter((r) => r.participantId !== participantId)
    registrations.value.set(courseId, updated)

    // Update IndexedDB cache
    await cacheRegistrationsLocally(courseId, updated)
  }

  /**
   * Clear registrations for a course
   */
  function clearCourse(courseId: number): void {
    registrations.value.delete(courseId)
    lastFetched.value.delete(courseId)
  }

  /**
   * Reset store
   */
  function $reset(): void {
    registrations.value.clear()
    lastFetched.value.clear()
    isLoading.value = false
    error.value = null
  }

  return {
    // State
    registrations,
    isLoading,
    error,

    // Getters
    getByCourseId,
    getActiveByCourseId,
    getForSessionDate,
    getCountByCourseId,
    getAllParticipants,
    getByParticipantId,

    // Actions
    fetchByCourse,
    createRegistration,
    promoteRegistration,
    removeRegistration,
    clearCourse,
    $reset,
  }
})
